import React from "react";
import { PolicyCalloutFrame } from "./PolicyCalloutFrame";
import Strings from "@/shared/utils/Strings.constants";
import Spot from "@/shared/components/PolicyCalloutTest/assets/spot.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBadgePercent, faBowlFood, faCircleCheck, faDogLeashed, faHeadset, faTooth } from "@fortawesome/pro-duotone-svg-icons";
import { faArrowUpRightFromSquare, faX } from "@fortawesome/pro-regular-svg-icons";
import { useModal } from "@/shared/contexts/ModalProvider";
import ComparisonTableModalContent from "./CompareModal";
import { cn } from "@/shared/utils";

export interface AggregatorComparisonTableProps {
    firstFeatureText?: string;
    title?: string;
    tagline?: string;
    showAccolades?: boolean;
    useWideColumns?: boolean;
    whySpotBadgeSuperscript?: string;
}

const AggregatorComparisonTable: React.FC<AggregatorComparisonTableProps> = ({
    firstFeatureText = Strings.AGGREGATOR_COMPARISON.FIRST_FEATURE_NATURAL_INTELLIGENCE,
    title,
    tagline,
    showAccolades,
    useWideColumns = false,
    whySpotBadgeSuperscript
}) => {
    const { showModal } = useModal();
    const CALLOUT_CONTENT = Strings.POLICY_CALLOUT_CONTENT;
    const AGG_STRINGS = Strings.AGGREGATOR_COMPARISON;
    const columnWidthClass = useWideColumns ? "w-[52px] lg:w-[132px] xl:w-[182px]" : "min-w-14 sm:min-w-16";

    const compareFeatures = [
        { feature: firstFeatureText, icon: faBadgePercent, symbol: "2" },
        { feature: AGG_STRINGS.FEATURE_DENTAL_ILLNESSES, icon: faTooth, symbol: "3" },
        { feature: AGG_STRINGS.FEATURE_PRESCRIPTION_FOOD, icon: faBowlFood, symbol: "4" },
        { feature: AGG_STRINGS.FEATURE_TELEHEALTH, icon: faHeadset },
        { feature: AGG_STRINGS.FEATURE_BEHAVIORAL_MODIFICATION, icon: faDogLeashed }
    ];

    const handleOpenModal = () => {
        showModal("comparison-table-modal", <ComparisonTableModalContent />, AGG_STRINGS.MODAL_HEADING, undefined, {
            contentWrapperClass: "pt-6 px-3 w-[92%]",
            closeButtonClass: "top-2 sm:top-4 p-0 sm:p-1 z-20"
        });
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleOpenModal();
        }
    };

    const TableRow: React.FC<{ feature: string; icon: any; symbol?: string }> = ({ feature, icon, symbol }) => {
        return (
            <div className="flex items-center justify-between ps-4">
                <div className="flex min-w-0 items-center gap-3 py-[6px]">
                    {icon && (
                        <div className="size-6 shrink-0">
                            <FontAwesomeIcon className="size-6 text-content-brand-b" icon={icon} />
                        </div>
                    )}
                    <span className="text-xs font-medium text-content-primary sm:text-sm">
                        {feature}
                        {symbol && <sup className="text-content-secondary">{symbol}</sup>}
                    </span>
                </div>
                <div className="flex shrink-0 items-center">
                    <div className={cn("flex items-center justify-center py-[2px]", columnWidthClass)}>
                        <div className="flex size-5 items-center justify-center">
                            <FontAwesomeIcon
                                className="size-4 [--fa-primary-color:#fff] [--fa-secondary-color:var(--bg-success-secondary)] [--fa-secondary-opacity:1]"
                                icon={faCircleCheck}
                            />
                        </div>
                    </div>
                    <div className={cn("flex items-center justify-center py-[2px]", columnWidthClass)}>
                        <div className="flex size-5 items-center justify-center">
                            <FontAwesomeIcon className="size-4" icon={faX} />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <PolicyCalloutFrame
            title={title}
            tagline={tagline}
            showAccolades={showAccolades}
            accoladesVariant="whySpot"
            whySpotBadgeSuperscript={whySpotBadgeSuperscript}
            showWhySpotBox={true}
            containerClassName=""
            titleClassName="lg:text-2xl lg:font-bold xl:text-start xl:text-3xl"
            contentClassName="px-[1px] pb-2 pt-4 mt-3"
        >
            <div>
                {/* Header Row */}
                <div className="flex items-center justify-end">
                    <div className={cn("flex items-center justify-center py-[2px]", columnWidthClass)}>
                        <Spot className="h-5" />
                    </div>
                    <div className={cn("flex items-center justify-center py-[2px] text-xs font-bold text-content-primary", columnWidthClass)}>Other</div>
                </div>

                <div className="py-2 pr-2 ps-4">
                    <div className="h-[1px] bg-background-disabled">&nbsp;</div>
                </div>

                {compareFeatures.map((item, index) => (
                    <TableRow key={index} feature={item.feature} icon={item.icon} symbol={item.symbol} />
                ))}

                <div className="py-2 pr-2 ps-4">
                    <div className="h-[1px] bg-background-disabled">&nbsp;</div>
                </div>

                <div className="flex items-center justify-between px-3">
                    <div
                        className="flex cursor-pointer gap-1 rounded focus:outline-none focus:ring-2 focus:ring-content-brand-b focus:ring-offset-2"
                        role="button"
                        tabIndex={0}
                        onClick={handleOpenModal}
                        onKeyDown={handleKeyDown}
                        aria-label={AGG_STRINGS.MORE_LINK_ARIA_LABEL}
                    >
                        <span className="text-xs font-medium text-content-brand-b underline">and much more</span>
                        <div className="flex size-4 items-center justify-center">
                            <FontAwesomeIcon className="size-3 text-content-brand-b" icon={faArrowUpRightFromSquare} />
                        </div>
                    </div>

                    <div className="flex items-center gap-3 py-1">
                        <div className="flex items-center gap-1">
                            <FontAwesomeIcon
                                className="size-3 [--fa-primary-color:#fff] [--fa-secondary-color:var(--bg-success-secondary)] [--fa-secondary-opacity:1]"
                                icon={faCircleCheck}
                            />
                            <span className="text-xs font-medium">{AGG_STRINGS.TABLE_LEGEND_COVERED}</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <FontAwesomeIcon className="size-3" icon={faX} />
                            <span className="text-xs font-medium">Not Covered</span>
                        </div>
                    </div>
                </div>
            </div>
        </PolicyCalloutFrame>
    );
};

export default AggregatorComparisonTable;
